import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import type maplibregl from 'maplibre-gl';
import type { FilterSpecification } from 'maplibre-gl';
import type { FeatureCollection, Geometry } from 'geojson';

import type { Client } from '@/clients/types';
import type { FeatureOverlayState } from '@/components/map/utils/mapLayers';
import { addOrbitLayer, ORBIT_LAYER_IDS } from '@/components/map/utils/mapLayerOrder';
import { fetchDmasForViewport } from '@/api/dmaApi';
import { attachViewportLoader, EMPTY_FEATURE_COLLECTION, setGeoJsonSourceData } from '@/utils/mapUtils';

export const DMA_SOURCE_ID = 'orbit-dma-source';

// Fetch guard: DMA bounding boxes at wider scales exceed the backend's 2-square-degree
// limit, so data only loads from zoom 11 — the District Meter Area semantic band start,
// where DMA layers first become visible (see semanticZoomLayers).
const DMA_MIN_ZOOM = 11;
const DMA_LABEL_MIN_ZOOM = 12;

function registerDmaSourceAndLayers(mlMap: maplibregl.Map, data: FeatureCollection, visible: boolean): void {
    const visibility = visible ? 'visible' : 'none';

    try {
        if (mlMap.getSource(DMA_SOURCE_ID)) {
            (mlMap.getSource(DMA_SOURCE_ID) as maplibregl.GeoJSONSource).setData(data);
        } else {
            // promoteId exposes each DMA's `ident` as the feature id so hover state can be
            // toggled per-feature via setFeatureState (see useDmaInteractions).
            mlMap.addSource(DMA_SOURCE_ID, { type: 'geojson', data, promoteId: 'ident' });
        }
    } catch {
        return;
    }

    if (!mlMap.getLayer(ORBIT_LAYER_IDS.dmaFill)) {
        try {
            addOrbitLayer(
                mlMap,
                {
                    id: ORBIT_LAYER_IDS.dmaFill,
                    type: 'fill',
                    source: DMA_SOURCE_ID,
                    minzoom: DMA_MIN_ZOOM,
                    layout: { visibility },
                    paint: {
                        'fill-color': '#007CFF',
                        // Brighten the fill while the cursor is over the DMA (feature-state hover).
                        'fill-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 0.35, 0.2],
                    },
                },
                'areaOverlays',
            );
        } catch {
            // Progressive enhancement — layer failures must not break the map.
        }
    }

    if (!mlMap.getLayer(ORBIT_LAYER_IDS.dmaOutline)) {
        try {
            addOrbitLayer(
                mlMap,
                {
                    id: ORBIT_LAYER_IDS.dmaOutline,
                    type: 'line',
                    source: DMA_SOURCE_ID,
                    minzoom: DMA_MIN_ZOOM,
                    layout: { visibility },
                    paint: {
                        'line-color': '#007CFF',
                        // Thicken the dashed border while the cursor is over the DMA (feature-state hover).
                        'line-width': ['case', ['boolean', ['feature-state', 'hover'], false], 2.5, 1.5],
                        'line-dasharray': [3, 3],
                    },
                },
                'areaOverlays',
            );
        } catch {
            // Progressive enhancement — layer failures must not break the map.
        }
    }

    if (!mlMap.getLayer(ORBIT_LAYER_IDS.dmaSelectedFill)) {
        try {
            addOrbitLayer(
                mlMap,
                {
                    id: ORBIT_LAYER_IDS.dmaSelectedFill,
                    type: 'fill',
                    source: DMA_SOURCE_ID,
                    minzoom: DMA_MIN_ZOOM,
                    layout: { visibility },
                    filter: ['==', ['get', 'ident'], ''] as FilterSpecification,
                    paint: {
                        'fill-color': '#1d4ed8',
                        'fill-opacity': 0.35,
                    },
                },
                'areaOverlays',
            );
        } catch {
            // Progressive enhancement — layer failures must not break the map.
        }
    }

    if (!mlMap.getLayer(ORBIT_LAYER_IDS.dmaSelectedOutline)) {
        try {
            addOrbitLayer(
                mlMap,
                {
                    id: ORBIT_LAYER_IDS.dmaSelectedOutline,
                    type: 'line',
                    source: DMA_SOURCE_ID,
                    minzoom: DMA_MIN_ZOOM,
                    layout: { visibility },
                    filter: ['==', ['get', 'ident'], ''] as FilterSpecification,
                    paint: {
                        'line-color': '#1e40af',
                        'line-width': 3,
                        'line-opacity': 1,
                    },
                },
                'areaOverlays',
            );
        } catch {
            // Progressive enhancement — layer failures must not break the map.
        }
    }

    if (!mlMap.getLayer(ORBIT_LAYER_IDS.dmaLabels)) {
        try {
            addOrbitLayer(
                mlMap,
                {
                    id: ORBIT_LAYER_IDS.dmaLabels,
                    type: 'symbol',
                    source: DMA_SOURCE_ID,
                    minzoom: DMA_LABEL_MIN_ZOOM,
                    layout: {
                        visibility,
                        'text-field': ['get', 'name'],
                        // OpenFreeMap's glyphs endpoint hosts "Noto Sans …"; without an explicit
                        // text-font MapLibre requests its default "Open Sans Regular,Arial Unicode
                        // MS Regular" stack, which 404s and logs glyph-range warnings.
                        'text-font': ['Noto Sans Regular'],
                        'text-size': 12,
                        'text-anchor': 'center',
                        'text-max-width': 8,
                    },
                    paint: {
                        'text-color': '#1d4ed8',
                        'text-halo-color': '#ffffff',
                        'text-halo-width': 1.5,
                    },
                },
                'businessLabels',
            );
        } catch {
            // Progressive enhancement — layer failures must not break the map.
        }
    }
}

/**
 * Loads DMA GeoJSON for the current viewport, registers the MapLibre GeoJSON
 * source and three layers (fill, outline, labels), and re-registers them after
 * every base map style change. Fetches on mount and on moveend (300ms debounce);
 * skips fetch when zoom is below DMA_MIN_ZOOM (10). Visibility is set at
 * registration time from `featureOverlays.districtMeterAreas`; subsequent toggle
 * changes are handled by `useMapLayerVisibility` via the populated
 * `districtMeterAreas.mapLayerIds`.
 *
 * Safe when:
 *   - No client is selected (clears source data, skips fetch).
 *   - DMA data has not loaded yet (source contains empty FeatureCollection).
 *   - Map layers do not exist (all operations guard with getLayer/getSource).
 *   - API requests fail inside the viewport loader; map continues working.
 */
export interface DistrictMeterAreasLayer {
    /** Full unclipped geometry of the loaded DMA with this `ident`, or null if not loaded. */
    getDmaGeometry: (ident: string) => Geometry | null;
    /** The full unclipped DMA collection loaded for the current viewport. Stable identity (reads a ref). */
    getDmasData: () => FeatureCollection;
    /**
     * Bumped when the viewport loader updates the collection (fetch success, bbox-cache
     * hit, below-min-zoom clear). Lets the stats-scope resolver re-run when DMA data
     * arrives after a pan settled — the resolver would otherwise read stale polygons.
     */
    dmasDataVersion: number;
}

export function useDistrictMeterAreasLayer(
    mapRef: RefObject<MapRef | null>,
    isMapLoaded: boolean,
    selectedClient: Client | null,
    featureOverlays: FeatureOverlayState,
    selectedDmaIdent: string | null = null,
    /**
     * Keeps the DMA data loaded even when the DMA overlay is toggled off. Used on
     * Find & Fix where the stats-scope resolver needs the polygons for centre-point
     * containment regardless of rendering visibility.
     */
    keepDataLoaded: boolean = false,
): DistrictMeterAreasLayer {
    const dmaDataRef = useRef<FeatureCollection>(EMPTY_FEATURE_COLLECTION);
    const [dmasDataVersion, setDmasDataVersion] = useState(0);

    const getDmasData = useCallback((): FeatureCollection => dmaDataRef.current, []);

    // Look up a loaded DMA's full geometry by ident. Reads the viewport data ref (not the
    // tile-clipped rendered features), so callers get the complete polygon. Stable identity
    // — the ref is mutated in place, so this never needs to change.
    const getDmaGeometry = useCallback((ident: string): Geometry | null => {
        const feature = dmaDataRef.current.features.find((f) => f.properties?.ident === ident);
        return feature?.geometry ?? null;
    }, []);

    // Keep refs so the registration/style-reload handlers can read current values
    // without those changes re-triggering the registration effect.
    const featureOverlaysRef = useRef(featureOverlays);
    const selectedDmaIdentRef = useRef(selectedDmaIdent);
    useLayoutEffect(() => {
        featureOverlaysRef.current = featureOverlays;
        selectedDmaIdentRef.current = selectedDmaIdent;
    });

    // Load DMA data for the current viewport when client/map-ready/enabled state changes.
    // Re-loads on map moveend (covers both pan and zoom end).
    // `keepDataLoaded` keeps the data (not the rendering) alive when the overlay is off.
    const loadData = featureOverlays.districtMeterAreas || keepDataLoaded;
    useEffect(() => {
        // The synchronous reset here is deliberately NOT version-bumped (the lint rule
        // forbids sync setState in effects): consumers read live data via getDmasData()
        // and re-resolve on client changes themselves; the version only needs to signal
        // asynchronous data arrivals, which onData below covers.
        dmaDataRef.current = EMPTY_FEATURE_COLLECTION;
        const mlMap = mapRef.current?.getMap();
        if (mlMap) setGeoJsonSourceData(mlMap, DMA_SOURCE_ID, EMPTY_FEATURE_COLLECTION);

        if (!selectedClient || !isMapLoaded) return;
        if (!loadData) return;

        return attachViewportLoader(mapRef, {
            sourceId: DMA_SOURCE_ID,
            minZoom: DMA_MIN_ZOOM,
            clientId: selectedClient.id,
            dataRef: dmaDataRef,
            fetchFn: fetchDmasForViewport,
            onData: () => {
                setDmasDataVersion((v) => v + 1);
            },
        });
    }, [selectedClient, isMapLoaded, loadData, mapRef]);

    // Register DMA source and layers when the map is ready, and re-register on
    // every style.load (base map switches reload all sources and layers).
    // After each registration, restore the current selection filter so the
    // highlight layer survives basemap changes.
    useEffect(() => {
        if (!isMapLoaded) return;
        const mlMap = mapRef.current?.getMap();
        if (!mlMap) return;

        const register = () => {
            registerDmaSourceAndLayers(mlMap, dmaDataRef.current, featureOverlaysRef.current.districtMeterAreas);
            const filter: FilterSpecification = ['==', ['get', 'ident'], selectedDmaIdentRef.current ?? ''];
            if (mlMap.getLayer(ORBIT_LAYER_IDS.dmaSelectedFill))
                mlMap.setFilter(ORBIT_LAYER_IDS.dmaSelectedFill, filter);
            if (mlMap.getLayer(ORBIT_LAYER_IDS.dmaSelectedOutline))
                mlMap.setFilter(ORBIT_LAYER_IDS.dmaSelectedOutline, filter);
        };

        register();
        mlMap.on('style.load', register);

        return () => {
            mlMap.off('style.load', register);
        };
    }, [isMapLoaded, mapRef]);

    // Update selection highlight filter whenever selectedDmaIdent changes.
    useEffect(() => {
        if (!isMapLoaded) return;
        const mlMap = mapRef.current?.getMap();
        if (!mlMap) return;
        const filter: FilterSpecification = ['==', ['get', 'ident'], selectedDmaIdent ?? ''];
        if (mlMap.getLayer(ORBIT_LAYER_IDS.dmaSelectedFill)) mlMap.setFilter(ORBIT_LAYER_IDS.dmaSelectedFill, filter);
        if (mlMap.getLayer(ORBIT_LAYER_IDS.dmaSelectedOutline))
            mlMap.setFilter(ORBIT_LAYER_IDS.dmaSelectedOutline, filter);
    }, [selectedDmaIdent, isMapLoaded, mapRef]);

    return { getDmaGeometry, getDmasData, dmasDataVersion };
}
